import client from '@appsvio/jira-rest-api-client/src/forgeFrontendClient';
import { IssueBean } from '@appsvio/jira-rest-api-client/src/generated-client/models/IssueBean';
import { SearchResults } from '@appsvio/jira-rest-api-client/src/generated-client/models/SearchResults';
import { events } from '@forge/bridge';

import {
    ACCOUNT_ID,
    ASSIGNEE_INDEX,
    CUSTOM_FIELD_PREFIX,
    GADGET_DATA_FETCHED,
    GROUPING_FIELDS,
    MAX_RESULTS,
    NAME,
    PRIORITY_INDEX,
    STATUS_CATEGORY_INDEX,
    STATUS_INDEX,
    UNASSIGNED,
} from '../constants';
import cacheClient from '../GadgetCacheClient';
import {
    AggregatedReport,
    Assignee,
    CustomFieldId,
    FetchingProgress,
    GadgetContext,
    IssueFields,
    Priority,
    Status,
    StatusCategory,
} from '../model';

const searchIssue = async (jql: string, startAt: number, fields: string[] = []) => {
    const requestParameters = {
        searchRequestBean: {
            jql,
            startAt,
            maxResults: MAX_RESULTS,
            fields: [...fields, ...GROUPING_FIELDS],
        },
    };

    try {
        return await client.getIssueSearchApi().searchForIssuesUsingJqlPost(requestParameters);
    } catch (error) {
        console.error('error: ', error);
        return {} as SearchResults;
    }
};

export const getAllIssuesByJql = async (
    gadgetContext: GadgetContext,
    jql: string,
    extendsFields: string[] = [],
    issues: IssueBean[] = [],
    startAt = 0
) => {
    try {
        const issuesResponse = await searchIssue(jql, startAt, extendsFields);
        await emitFetchingProgress(gadgetContext, issuesResponse);
        issues.push(...(issuesResponse?.issues ? issuesResponse.issues : []));
        if (issuesResponse.startAt + MAX_RESULTS < issuesResponse.total) {
            await getAllIssuesByJql(gadgetContext, jql, extendsFields, issues, issuesResponse.startAt + MAX_RESULTS);
        }
    } catch (e) {
        console.log('error: ', e);
    }

    return issues;
};

export const getIssueFieldsByJqlForGadget = async (context: GadgetContext) => {
    const { jql, fieldId } = context.extension.gadgetConfiguration;
    const issues = await getAllIssuesByJql(context, prepareJql(fieldId, jql), [fieldId]);
    const issuesFields = issues.map(issue => issue.fields) as IssueFields[];
    const aggregatedReport = preparePriorityReport(issuesFields, fieldId);
    await cacheClient.set(context, aggregatedReport);
    return aggregatedReport;
};

const prepareJql = (fieldId: string, jql: string) => {
    return `${getCustomFieldJQLName(fieldId)} IS NOT EMPTY ${jql ? `AND ${jql}` : ''}`;
};

export const getCustomFieldJQLName = (fieldId: string) => {
    const customFieldIdentifier = fieldId.replace(CUSTOM_FIELD_PREFIX, '');
    return `cf[${customFieldIdentifier}]`;
};

const emitFetchingProgress = async (gadgetContext: GadgetContext, issuesResponse: SearchResults) => {
    const payload: FetchingProgress = {
        isFetching: issuesResponse.startAt + MAX_RESULTS < issuesResponse.total,
        total: issuesResponse.total,
        currentNumber: issuesResponse.startAt + MAX_RESULTS,
        gadgetId: gadgetContext.extension.gadget.id,
    };
    await events.emit(GADGET_DATA_FETCHED, payload);
};

const preparePriorityReport = (issuesFields: IssueFields[], fieldId: CustomFieldId) => {
    return issuesFields.reduce((previousValue, currentValue) => {
        let previousValueCopy = { ...previousValue };

        currentValue[fieldId].ids.forEach(id => {
            if (!previousValueCopy[id]) previousValueCopy = { ...previousValueCopy, [id]: [{}, {}, {}, {}] };
            addValueToAggregatedReport(previousValueCopy, currentValue.priority, NAME, PRIORITY_INDEX, id);
            addValueToAggregatedReport(previousValueCopy, currentValue.status, NAME, STATUS_INDEX, id);
            addValueToAggregatedReport(previousValueCopy, currentValue.status.statusCategory, NAME, STATUS_CATEGORY_INDEX, id);
            addValueToAggregatedReport(previousValueCopy, currentValue.assignee, ACCOUNT_ID, ASSIGNEE_INDEX, id);
        });

        return previousValueCopy;
    }, {} as AggregatedReport);
};

const addValueToAggregatedReport = <T extends Priority | Status | StatusCategory | Assignee>(
    previousAccValue: AggregatedReport,
    field: T,
    valueParam: string,
    index: number,
    lastOptionId: string
) => {
    if (field) {
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        addSingleValueToReport(previousAccValue, field[valueParam], index, lastOptionId);
    } else if (isAssigneeIndex(index)) {
        addSingleValueToReport(previousAccValue, UNASSIGNED, index, lastOptionId);
    }
};

const addSingleValueToReport = (previousAccValue: AggregatedReport, value: string, index: number, lastOptionId: string) => {
    if (previousAccValue[lastOptionId][index][value]) {
        previousAccValue[lastOptionId][index][value]++;
    } else {
        previousAccValue[lastOptionId][index][value] = 1;
    }
};

const isAssigneeIndex = (index: number) => index === ASSIGNEE_INDEX;
